import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Chip,
  Tooltip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  LockReset as ResetIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem, GridSortModel } from '@mui/x-data-grid';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { format } from 'date-fns';
import ApiService from '../services/api';
import { User, CreateUserRequest, Account, UserSearchParams } from '../types';

// Validation schema
const userSchema: yup.ObjectSchema<CreateUserRequest> = yup.object({
  account_id: yup.string().required('Account is required'),
  username: yup.string().required('Username is required').min(3, 'Username must be at least 3 characters'),
  email: yup.string().email('Invalid email').required('Email is required'),
  name: yup.string().required('Full name is required'),
  send_email: yup.boolean().default(true),
  temporary_password: yup.string().optional(),
  additional_attributes: yup.object<Record<string, string>>().optional().default({}),
}) as yup.ObjectSchema<CreateUserRequest>;

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useState<UserSearchParams>({
    account_id: '',
    limit: 50,
  });
  const [pageSize, setPageSize] = useState<number>(50);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [paginationHistory, setPaginationHistory] = useState<(string | undefined)[]>([undefined]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalFound, setTotalFound] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState(600);
  const headerRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: 'disable' | 'delete' | null;
    username: string;
    accountId: string;
    currentStatus?: string;
  }>({
    open: false,
    type: null,
    username: '',
    accountId: '',
  });
  const [updatingUsers, setUpdatingUsers] = useState<Set<string>>(new Set());

  const { control, handleSubmit, reset, formState: { errors } } = useForm<CreateUserRequest>({
    resolver: yupResolver(userSchema),
    defaultValues: {
      account_id: '',
      username: '',
      email: '',
      name: '',
      send_email: true,
      additional_attributes: {},
    },
  });

  useEffect(() => {
    loadAccounts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate dynamic height for DataGrid
  const calculateHeight = useCallback(() => {
    if (headerRef.current) {
      const headerHeight = headerRef.current.offsetHeight;
      const windowHeight = window.innerHeight;
      const padding = 100; // Leave some padding at bottom
      const newHeight = Math.max(400, windowHeight - headerHeight - padding);
      setContainerHeight(newHeight);
    }
  }, []);

  useEffect(() => {
    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, [calculateHeight]);

  // Recalculate height when alerts appear/disappear
  useEffect(() => {
    calculateHeight();
  }, [error, success, calculateHeight]);

  const loadAccounts = async () => {
    try {
      const data = await ApiService.getAccounts();
      setAccounts(data);
      if (data.length > 0 && !searchParams.account_id) {
        setSearchParams(prev => ({ ...prev, account_id: data[0].account_id }));
        searchUsers({ ...searchParams, account_id: data[0].account_id }, true);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load accounts';
      setError(errorMessage);
    }
  };

  const searchUsers = useCallback(async (params: UserSearchParams = searchParams, resetPagination: boolean = false) => {
    if (!params.account_id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.searchUsers(params);
      setUsers(data.users);
      setNextToken(data.next_token);
      setTotalFound(data.total_found || data.count);
      
      // Reset pagination history when starting a new search
      if (resetPagination) {
        setPaginationHistory([undefined]);
        setCurrentPage(0);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to search users';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  const debounceTimeoutRef = useRef<NodeJS.Timeout>();

  // Debounced search function
  const debouncedSearch = useCallback(
    (params: UserSearchParams) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        searchUsers(params, true);
      }, 300); // 300ms delay
    },
    [searchUsers]
  );

  const handleNextPage = () => {
    if (!nextToken) return;
    
    const newParams = { ...searchParams, next_token: nextToken };
    const newPage = currentPage + 1;
    
    // Add current next_token to pagination history if it's a new page
    if (paginationHistory.length === newPage) {
      setPaginationHistory([...paginationHistory, nextToken]);
    }
    
    setCurrentPage(newPage);
    searchUsers(newParams, false);
  };

  const handlePreviousPage = () => {
    if (currentPage === 0) return;

    const newPage = currentPage - 1;
    const token = paginationHistory[newPage];
    const newParams = { ...searchParams, next_token: token };

    setCurrentPage(newPage);
    searchUsers(newParams, false);
  };

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);

    // Convert GridSortModel to backend parameters
    let newParams = { ...searchParams };

    if (newSortModel.length > 0 && newSortModel[0].sort) {
      newParams.sort_by = newSortModel[0].field;
      newParams.sort_order = newSortModel[0].sort as 'asc' | 'desc';
    } else {
      // Remove sorting if no sort is applied
      delete newParams.sort_by;
      delete newParams.sort_order;
    }

    setSearchParams(newParams);
    searchUsers(newParams, true);
  };

  const handleExportCsv = async () => {
    if (!searchParams.account_id || exporting) {
      return;
    }

    const { limit: _limit, next_token: _nextToken, ...exportParams } = searchParams;

    try {
      setExporting(true);
      setError(null);
      setSuccess(null);

      const { blob, filename } = await ApiService.exportUsersCsv(exportParams);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(`User export ready: ${filename}`);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to export users';
      setError(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  const handleCreateUser = async (data: CreateUserRequest) => {
    try {
      setError(null);
      await ApiService.createUser(data);
      setSuccess('User created successfully');
      setDialogOpen(false);
      reset();
      await searchUsers(searchParams, true);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create user';
      setError(errorMessage);
    }
  };

  const handleResetPassword = async (accountId: string, username: string) => {
    if (!window.confirm('Are you sure you want to reset this user\'s password?')) {
      return;
    }

    try {
      setError(null);
      const result = await ApiService.resetUserPassword(accountId, username, { send_email: true });
      setSuccess(`Password reset for ${username}. New password: ${result.new_password}`);
      await searchUsers(searchParams, false);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to reset password';
      setError(errorMessage);
    }
  };

  const handleToggleUserStatus = (accountId: string, username: string, currentEnabled: boolean) => {
    setConfirmDialog({
      open: true,
      type: 'disable',
      username,
      accountId,
      currentStatus: currentEnabled ? 'ENABLED' : 'DISABLED',
    });
  };

  const handleDeleteUser = (accountId: string, username: string) => {
    setConfirmDialog({
      open: true,
      type: 'delete',
      username,
      accountId,
    });
  };

  const confirmAction = async () => {
    if (!confirmDialog.type) return;

    try {
      setError(null);
      setConfirmDialog({ ...confirmDialog, open: false });

      if (confirmDialog.type === 'disable') {
        const newStatus = confirmDialog.currentStatus === 'ENABLED' ? 'DISABLED' : 'ENABLED';
        const action = newStatus === 'ENABLED' ? 'enable' : 'disable';
        
        // Add loading state
        const userKey = `${confirmDialog.accountId}:${confirmDialog.username}`;
        setUpdatingUsers(prev => new Set(prev).add(userKey));
        
        try {
          await ApiService.updateUserStatus(confirmDialog.accountId, confirmDialog.username, {
            status: newStatus,
            send_email: true,
          });
          setSuccess(`User ${action}d successfully`);
          
          // Refresh the user list to show updated status
          await searchUsers(searchParams, false);
        } finally {
          // Remove loading state
          setUpdatingUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(userKey);
            return newSet;
          });
        }
      } else if (confirmDialog.type === 'delete') {
        await ApiService.deleteUser(confirmDialog.accountId, confirmDialog.username);
        setSuccess('User deleted successfully');
      }

      await searchUsers(searchParams, false);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || `Failed to ${confirmDialog.type} user`;
      setError(errorMessage);
    } finally {
      setConfirmDialog({
        open: false,
        type: null,
        username: '',
        accountId: '',
      });
    }
  };

  const cancelAction = () => {
    setConfirmDialog({
      open: false,
      type: null,
      username: '',
      accountId: '',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ENABLED':
        return 'success';
      case 'DISABLED':
        return 'error';
      case 'FORCE_CHANGE_PASSWORD':
        return 'warning';
      case 'UNCONFIRMED':
        return 'info';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'username',
      headerName: 'Username',
      width: 200,
      sortable: true,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      sortable: true,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      sortable: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      sortable: true,
      renderCell: (params) => {
        const getDisplayStatus = () => {
          if (!params.row.enabled) {
            return { label: 'Disabled', color: 'error' };
          }
          
          switch (params.value) {
            case 'CONFIRMED':
              return { label: 'Active', color: 'success' };
            case 'FORCE_CHANGE_PASSWORD':
              return { label: 'Pending Password Change', color: 'warning' };
            case 'UNCONFIRMED':
              return { label: 'Unconfirmed', color: 'info' };
            default:
              return { label: params.value, color: getStatusColor(params.value) };
          }
        };
        
        const { label, color } = getDisplayStatus();
        
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={label}
              color={color as any}
              size="small"
              variant={params.row.enabled ? 'filled' : 'outlined'}
            />
            {updatingUsers.has(`${params.row.account_id}:${params.row.username}`) && (
              <Chip
                label="Updating..."
                color="info"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      sortable: true,
      renderCell: (params) => (
        params.value ? format(new Date(params.value), 'MMM dd, yyyy') : 'N/A'
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="Reset password">
              <ResetIcon />
            </Tooltip>
          }
          label="Reset Password"
          onClick={() => handleResetPassword(params.row.account_id, params.row.username)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title={`Toggle user ${params.row.enabled ? 'disabled' : 'enabled'}`}>
              <FormControlLabel
                control={
                  <Switch
                    checked={params.row.enabled}
                    onChange={() => handleToggleUserStatus(params.row.account_id, params.row.username, params.row.enabled)}
                    disabled={updatingUsers.has(`${params.row.account_id}:${params.row.username}`)}
                    color="primary"
                    size="small"
                    sx={{
                      '& .MuiSwitch-thumb': {
                        ...(updatingUsers.has(`${params.row.account_id}:${params.row.username}`) && {
                          animation: 'spin 1s linear infinite',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                          },
                        }),
                      },
                    }}
                  />
                }
                label=""
                sx={{ margin: 0 }}
              />
            </Tooltip>
          }
          label="Toggle Status"
          onClick={() => handleToggleUserStatus(params.row.account_id, params.row.username, params.row.enabled)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Delete user permanently">
              <DeleteIcon color="error" />
            </Tooltip>
          }
          label="Delete"
          onClick={() => handleDeleteUser(params.row.account_id, params.row.username)}
        />,
      ],
    },
  ];

  return (
    <Box>
      <Box ref={headerRef}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4">
            Users
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => searchUsers(searchParams, true)}
              disabled={!searchParams.account_id}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCsv}
              disabled={!searchParams.account_id || exporting}
            >
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Create User
            </Button>
          </Box>
        </Box>

      {/* Status Summary */}
      {searchParams.account_id && users.length > 0 && (
        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <Chip
            label={`${users.filter(u => u.enabled && u.status === 'CONFIRMED').length} Active Users`}
            color="success"
            variant="outlined"
          />
          <Chip
            label={`${users.filter(u => !u.enabled).length} Disabled Users`}
            color="error"
            variant="outlined"
          />
          <Chip
            label={`${users.filter(u => u.enabled && u.status === 'FORCE_CHANGE_PASSWORD').length} Password Reset Required`}
            color="warning"
            variant="outlined"
          />
          <Chip
            label={`${users.filter(u => u.enabled && u.status === 'UNCONFIRMED').length} Unconfirmed`}
            color="info"
            variant="outlined"
          />
        </Box>
      )}

      {/* Search Filters */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Account</InputLabel>
          <Select
            value={searchParams.account_id}
            label="Account"
            onChange={(e) => {
              const newParams = { ...searchParams, account_id: e.target.value };
              setSearchParams(newParams);
              searchUsers(newParams, true);
            }}
          >
            {accounts.map((account) => (
              <MenuItem key={account.account_id} value={account.account_id}>
                {account.deployment_name} ({account.environment})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Search Username"
          value={searchParams.username || ''}
          onChange={(e) => {
            const newParams = { ...searchParams, username: e.target.value };
            setSearchParams(newParams);
            if (newParams.account_id) {
              debouncedSearch(newParams);
            }
          }}
          sx={{ minWidth: 200 }}
          placeholder="Type to search..."
        />

        <TextField
          label="Search Email"
          value={searchParams.email || ''}
          onChange={(e) => {
            const newParams = { ...searchParams, email: e.target.value };
            setSearchParams(newParams);
            if (newParams.account_id) {
              debouncedSearch(newParams);
            }
          }}
          sx={{ minWidth: 200 }}
          placeholder="Type to search..."
        />

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={searchParams.status || ''}
            label="Status"
            onChange={(e) => {
              const newParams = { ...searchParams, status: e.target.value || undefined };
              setSearchParams(newParams);
              if (newParams.account_id) {
                searchUsers(newParams, true);
              }
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="DISABLED">Disabled</MenuItem>
            <MenuItem value="CONFIRMED">Confirmed</MenuItem>
            <MenuItem value="UNCONFIRMED">Unconfirmed</MenuItem>
            <MenuItem value="FORCE_CHANGE_PASSWORD">Force Change Password</MenuItem>
            <MenuItem value="ARCHIVED">Archived</MenuItem>
            <MenuItem value="COMPROMISED">Compromised</MenuItem>
            <MenuItem value="UNKNOWN">Unknown</MenuItem>
            <MenuItem value="RESET_REQUIRED">Reset Required</MenuItem>
          </Select>
        </FormControl>
      </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
      </Box>

      {/* Pagination Controls */}
      {searchParams.account_id && users.length > 0 && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" gap={2} alignItems="center">
            <Typography variant="body2" color="textSecondary">
              Showing {users.length} users
              {totalFound > users.length && ` (${totalFound} total found)`}
              {currentPage > 0 && ` - Page ${currentPage + 1}`}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Page Size</InputLabel>
              <Select
                value={pageSize}
                label="Page Size"
                onChange={(e) => {
                  const newSize = Number(e.target.value);
                  setPageSize(newSize);
                  const newParams = { ...searchParams, limit: newSize };
                  setSearchParams(newParams);
                  searchUsers(newParams, true);
                }}
              >
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={250}>250</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<NavigateBeforeIcon />}
              onClick={handlePreviousPage}
              disabled={currentPage === 0 || loading}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              size="small"
              endIcon={<NavigateNextIcon />}
              onClick={handleNextPage}
              disabled={!nextToken || loading}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ height: containerHeight, width: '100%' }}>
        <DataGrid
          rows={users}
          columns={columns}
          getRowId={(row) => `${row.account_id}-${row.username}`}
          loading={loading}
          disableRowSelectionOnClick
          hideFooter
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
        />
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(handleCreateUser)}>
          <DialogTitle>Create New User</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Controller
                name="account_id"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Account</InputLabel>
                    <Select
                      {...field}
                      label="Account"
                      error={!!errors.account_id}
                    >
                      {accounts.map((account) => (
                        <MenuItem key={account.account_id} value={account.account_id}>
                          {account.deployment_name} ({account.environment})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Username"
                    error={!!errors.username}
                    helperText={errors.username?.message}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Full Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    fullWidth
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              Create User
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={cancelAction}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.type === 'delete' ? 'Delete User' : 'Change User Status'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.type === 'delete' 
              ? `Are you sure you want to permanently delete user "${confirmDialog.username}"? This action cannot be undone.`
              : `Are you sure you want to ${confirmDialog.currentStatus === 'ENABLED' ? 'disable' : 'enable'} user "${confirmDialog.username}"?`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelAction}>Cancel</Button>
          <Button 
            onClick={confirmAction} 
            variant="contained" 
            color={confirmDialog.type === 'delete' ? 'error' : 'primary'}
          >
            {confirmDialog.type === 'delete' 
              ? 'Delete User' 
              : (confirmDialog.currentStatus === 'ENABLED' ? 'Disable' : 'Enable')
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;